import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { formatBreakdownDate, RATE_VARIANCE_HINT } from "../lib/tripPricingDisplay";

/**
 * Collapsible trip rental subtotal with optional per-day expansion.
 */
export default function TripChargesGroup({
  money,
  days,
  subtotal,
  avgDaily,
  dayBreakdown = [],
  dailyRate,
  listPrice,
  showListVsAvg = false,
  testid = "trip-charges-group",
}) {
  const [open, setOpen] = useState(false);
  const hasDays = dayBreakdown.length > 0;
  const canExpand = hasDays && dayBreakdown.length > 1;

  const summaryMeta = hasDays && avgDaily != null
    ? `${days} ${days === 1 ? "day" : "days"} · avg ${money(avgDaily)}/day`
    : days > 0
      ? `${money(dailyRate ?? 0)} × ${days} ${days === 1 ? "day" : "days"}`
      : null;

  const toggle = () => {
    if (canExpand) setOpen((v) => !v);
  };

  return (
    <div className="rounded-lg border border-[var(--zc-border)] bg-[var(--zc-bg-2)]/40 overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={toggle}
        disabled={!canExpand}
        aria-expanded={canExpand ? open : undefined}
        className={
          "w-full flex items-start justify-between gap-3 px-3 py-2.5 text-left text-sm " +
          (canExpand ? "hover:bg-[var(--zc-bg-2)] cursor-pointer" : "cursor-default")
        }
        data-testid={`${testid}-toggle`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-[var(--zc-text)] font-semibold">Rental subtotal</span>
            {canExpand && (
              <ChevronDown
                size={14}
                className={"text-[var(--zc-text-3)] shrink-0 transition-transform " + (open ? "rotate-180" : "")}
              />
            )}
          </div>
          {summaryMeta && (
            <span className="block zc-meta font-normal mt-0.5 text-[var(--zc-text-3)]">{summaryMeta}</span>
          )}
          {showListVsAvg && listPrice && (
            <span className="block zc-meta font-normal mt-0.5 text-[var(--zc-text-3)]">
              List price{" "}
              {listPrice.isPromo ? (
                <><s>{money(listPrice.value)}</s> · {money(listPrice.current)}</>
              ) : (
                money(listPrice.value)
              )}
              /day
            </span>
          )}
          {!canExpand && hasDays && dayBreakdown.length === 1 && (
            <span className="block zc-meta font-normal mt-0.5 text-[var(--zc-text-3)]">
              {formatBreakdownDate(dayBreakdown[0].date)} · {money(dayBreakdown[0].price)}
            </span>
          )}
          {canExpand && !open && (
            <span className="block zc-meta font-normal mt-0.5 text-[var(--zc-accent)]">
              Tap to see per-day rates
            </span>
          )}
        </div>
        <span className="text-[var(--zc-text)] font-semibold shrink-0 pt-0.5">{money(subtotal)}</span>
      </button>

      {canExpand && open && (
        <div
          className="px-3 pb-2.5 pt-0 border-t border-[var(--zc-border)] space-y-1.5"
          data-testid={`${testid}-days`}
        >
          <div className="pt-2 eyebrow text-2xs">Per-day rates</div>
          {dayBreakdown.map(({ date, price }) => (
            <div key={date} className="flex justify-between gap-3 text-xs">
              <span className="text-[var(--zc-text-3)]">{formatBreakdownDate(date)}</span>
              <span className="text-[var(--zc-text-2)] font-medium">{money(price)}</span>
            </div>
          ))}
          <p className="text-2xs text-[var(--zc-text-3)] pt-1" title={RATE_VARIANCE_HINT}>
            {RATE_VARIANCE_HINT}
          </p>
        </div>
      )}
    </div>
  );
}
